/**
 * AssignPlanFromCatalogModal
 *
 * Modal for assigning a specific plan to multiple organizations.
 * Opened from the Plans tab when clicking "Assign to Orgs" on a plan card.
 * Allows multi-selecting organizations to receive the plan.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  BuildingOfficeIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  CreditCardIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useSubscriptionStore, SubscriptionPlan, Subscription } from '@/stores/subscriptionStore';
import { apiService } from '@/services/api';
import toast from 'react-hot-toast';
import { formatPrice, formatLimit, getEffectivePrice } from '@/utils/subscriptionFormat';
import { TIER_DOT_CLASSES, tierClasses } from '../../utils/planTiers';

interface Organization {
  id: string;
  name: string;
  subdomain?: string;
}

interface AssignPlanFromCatalogModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: SubscriptionPlan;
  onSuccess?: () => void;
}

export function AssignPlanFromCatalogModal({
  isOpen,
  onClose,
  plan,
  onSuccess
}: AssignPlanFromCatalogModalProps) {
  const { allSubscriptions, bulkAssignPlan, fetchAllSubscriptions } = useSubscriptionStore();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedOrgIds, setSelectedOrgIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState<'all' | 'no-subscription' | 'different-plan'>('all');

  // Fetch organizations on mount
  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!isOpen) return;

      setLoading(true);
      try {
        const response = await apiService.get<{ success: boolean; data: Organization[] }>(
          '/v1/platform/organizations'
        );
        setOrganizations(response.data);
        // Also refresh subscriptions for accurate filtering
        fetchAllSubscriptions();
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
        toast.error('Failed to load organizations');
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [isOpen, fetchAllSubscriptions]);

  // Create a map of orgId -> subscription for quick lookup
  const subscriptionsByOrg = useMemo(() => {
    const map = new Map<string, Subscription>();
    allSubscriptions.forEach(sub => {
      map.set(sub.organizationId, sub);
    });
    return map;
  }, [allSubscriptions]);

  // Filter organizations based on search and filter mode
  const filteredOrganizations = useMemo(() => {
    return organizations.filter(org => {
      // Search filter
      const matchesSearch = searchQuery === '' ||
        org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        org.subdomain?.toLowerCase().includes(searchQuery.toLowerCase());

      if (!matchesSearch) return false;

      // Filter mode
      const subscription = subscriptionsByOrg.get(org.id);

      switch (filterMode) {
        case 'no-subscription':
          return !subscription || !subscription.planId;
        case 'different-plan':
          return !subscription || subscription.planId !== plan.id;
        default:
          return true;
      }
    });
  }, [organizations, searchQuery, filterMode, subscriptionsByOrg, plan.id]);

  const toggleOrganization = (orgId: string) => {
    setSelectedOrgIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orgId)) {
        newSet.delete(orgId);
      } else {
        newSet.add(orgId);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    const allIds = new Set(filteredOrganizations.map(org => org.id));
    setSelectedOrgIds(allIds);
  };

  const clearSelection = () => {
    setSelectedOrgIds(new Set());
  };

  const handleSubmit = async () => {
    if (selectedOrgIds.size === 0) return;

    setSubmitting(true);
    try {
      const orgIds = Array.from(selectedOrgIds);
      const result = await bulkAssignPlan(orgIds, plan.id);

      if (result.failed.length === 0) {
        toast.success(`Plan "${plan.name}" assigned to ${result.success.length} organization(s)`);
      } else if (result.success.length > 0) {
        toast.success(`Plan assigned to ${result.success.length} organization(s)`);
        toast.error(`Failed for ${result.failed.length} organization(s)`);
      } else {
        toast.error('Failed to assign plan to any organization');
      }

      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Failed to assign plan:', error);
      toast.error(error.response?.data?.error || 'Failed to assign plan');
    } finally {
      setSubmitting(false);
    }
  };

  // formatPrice, formatLimit, getEffectivePrice imported from shared utils

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CreditCardIcon className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Assign Plan to Organizations</h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`w-2 h-2 rounded-full ${tierClasses(TIER_DOT_CLASSES, plan.tier, 'bg-gray-500')}`} />
                  <span className="text-sm text-gray-600">
                    {plan.name} - {formatPrice(getEffectivePrice(plan), plan.pricing.billingCycle)}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Plan Summary */}
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <div className="flex gap-6 text-gray-600">
                <span>{formatLimit(plan.included.encountersPerMonth)} encounters/mo</span>
                <span>{formatLimit(plan.included.transcriptionMinutes)} minutes</span>
                <span>{formatLimit(plan.included.users)} users</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-280px)]">
            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search organizations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <select
                value={filterMode}
                onChange={(e) => setFilterMode(e.target.value as typeof filterMode)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="all">All Organizations</option>
                <option value="no-subscription">Without Subscription</option>
                <option value="different-plan">Different Plan</option>
              </select>
            </div>

            {/* Selection Controls */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-600">
                {selectedOrgIds.size} of {filteredOrganizations.length} selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={selectAll}
                  className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                >
                  Select All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={clearSelection}
                  className="text-sm text-gray-600 hover:text-gray-800 font-medium"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Organizations List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : filteredOrganizations.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <BuildingOfficeIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No organizations match your criteria.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredOrganizations.map((org) => {
                  const isSelected = selectedOrgIds.has(org.id);
                  const subscription = subscriptionsByOrg.get(org.id);
                  const hasSamePlan = subscription?.planId === plan.planId;

                  return (
                    <button
                      key={org.id}
                      onClick={() => toggleOrganization(org.id)}
                      disabled={hasSamePlan}
                      className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                        hasSamePlan
                          ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                          : isSelected
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isSelected ? 'bg-purple-500' : 'bg-gray-100'
                        }`}>
                          {isSelected ? (
                            <CheckIcon className="h-5 w-5 text-white" />
                          ) : (
                            <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-gray-900">{org.name}</p>
                          {org.subdomain && (
                            <p className="text-xs text-gray-500">{org.subdomain}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        {hasSamePlan ? (
                          <span className="text-xs text-green-600 font-medium">Already assigned</span>
                        ) : subscription?.planId ? (
                          <span className="text-xs text-gray-500">
                            Current: {subscription.planName || subscription.planId}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">No subscription</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="text-sm text-gray-600">
              {selectedOrgIds.size > 0 && (
                <span>
                  Assigning <strong>{plan.name}</strong> to {selectedOrgIds.size} organization{selectedOrgIds.size !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={selectedOrgIds.size === 0 || submitting}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4" />
                    Assign to {selectedOrgIds.size} Org{selectedOrgIds.size !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default AssignPlanFromCatalogModal;
